import './polyfills' // This import needs to come first
// Zone rules the browser's timezone database has not caught up with, patched
// into dayjs before anything renders a time.
import '@sportninja/common/ts-utils/registerPermanentDst'
import 'react-phone-input-2/lib/style.css'
import React from 'react'
import { I18nContext, I18nManager } from '@shopify/react-i18n'
import { createRoot } from 'react-dom/client'
import rootReducer from '@sportninja/common/reducers'
import rootSagaCreator from '@sportninja/common/sagas'
import configureStore from '@sportninja/common/store'
import { Provider } from 'react-redux'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@sportninja/common/config/queryClient'
import { ThemeProvider } from './configs'

import history from './history'
import App from './pages/App'
import { NotificationsPanelProvider } from './components/NotificationsPanelContext'

// This must come before App component import. These are the base imports that are
// overridden by component-specific styles that get imported via <App />
import './index.scss'
import './utility.scss'

// Smart banner app
import SmartBanner from 'react-smartbanner'
import '../../node_modules/react-smartbanner/dist/main.css'
import { init } from './utils/sentry'
import { App as AntApp } from 'antd'
import { announceSportsPayCallback } from './features/account/sportsPaySignUp/announceSportsPayCallback'

// Google tag manager
window.dataLayer = window.dataLayer || []

const locale = 'en'
const i18nManager = new I18nManager({
  locale,
  onError(error) {
    console.log(error)
  },
})

init()

const store = configureStore(rootReducer, rootSagaCreator(), history)

/*
 * SportsPay's registration callback loads this app inside the embedded frame.
 * Tell the opener as early as possible so it can close the frame.
 *
 * The app still renders normally afterwards — deliberately. Skipping the render
 * would leave a BLANK frame for any other embed that ever loaded a URL carrying
 * this param, and the app is legitimately framed elsewhere (the plugin/widget
 * embed). A brief render of a frame that is about to close is the safer trade.
 */
announceSportsPayCallback()

const container = document.getElementById('root')
const root = createRoot(container)

root.render(
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AntApp
          message={{
            duration: 5,
            className: 'no-transition',
          }}
          style={null}
        >
          <I18nContext.Provider value={i18nManager}>
            <NotificationsPanelProvider>
              <App history={history} />
            </NotificationsPanelProvider>
          </I18nContext.Provider>
          <SmartBanner
            title={'SportNinja'}
            position={'bottom'}
            daysHidden={1}
            daysReminder={1}
          />
        </AntApp>
      </ThemeProvider>
    </QueryClientProvider>
  </Provider>
)
